import styled from "@emotion/styled";
import { useEnsuredForwardedRef } from "@smartsheet/lodestar-utils";
import React, { forwardRef, HTMLAttributes, ReactNode, useContext } from "react";
import { TabPanel as BaseTabPanel } from "reakit/Tab";
import { getFocusRingStyles } from "../../util/focusRing";
import { LodestarCoreTheme, ThemeMode } from "../../util/theme";
import { withFocusToggleClassName } from "../../util/withFocusClassName";
import { TabsContext } from "./TabsContext";
import { findIndex } from "./findIndex";

interface StyleProps {
    themeMode: ThemeMode;
}

const StyledTabPanel = withFocusToggleClassName(
    styled(BaseTabPanel, {
        shouldForwardProp: (prop) => prop !== "themeMode",
    })<StyleProps>(({ themeMode }) =>
        getFocusRingStyles({
            theme: themeMode,
            offset: 4,
            radius: 4,
            includeFocusPseudoClass: true,
        })
    )
);
StyledTabPanel.displayName = "StyledTabPanel";

export interface TabPanelProps extends HTMLAttributes<HTMLElement> {
    /**
     * The children to render inside this Tab.
     */
    children: ReactNode;

    /**
     * If an id isn't provided, one will be automatically generated.
     * This is used to connect the TabPanel to its respective Tab.
     */
    id?: string;
}

export const TabPanel = forwardRef<HTMLDivElement, TabPanelProps>(({ children, ...rest }, parentRef) => {
    const theme = useContext(LodestarCoreTheme);
    const { onChange: _0, onlyRenderActiveTabPanelChildren, ...tab } = React.useContext(TabsContext);
    const ref = useEnsuredForwardedRef(parentRef);

    const selectedPanelIndex = findIndex(tab.panels, (panel) => panel.groupId === tab.selectedId);
    // It's important that the ref we use here is one that we ensure is always present, otherwise
    // Reakit will create one for us. Using useEnsuredForwardedRef allows us to create our own
    // primary ref here, sync that ref to the user's code if they provide one, and also force Reakit
    // to use the ref that we own here!
    const isSelected = tab.panels[selectedPanelIndex]?.ref.current === ref.current;

    return (
        <StyledTabPanel {...tab} {...rest} themeMode={theme} ref={ref}>
            {onlyRenderActiveTabPanelChildren ? (isSelected ? children : null) : children}
        </StyledTabPanel>
    );
});
TabPanel.displayName = "TabPanel";
